import { useEffect, useRef, useState } from 'react';

interface ServiceAreasProps {
  heading?: string;
  description?: string;
  areas?: string[];
}

const defaultAreas = [
  'Philadelphia, PA',
  'Northeast Philadelphia',
  'South Philadelphia',
  'Kensington',
  'Frankford',
  'Fishtown',
  'Port Richmond',
  'Mayfair',
  'Cheltenham Township',
  'Bensalem',
  'Levittown',
  'Bristol Township',
  'Pennsauken, NJ',
  'Cherry Hill, NJ',
  'Haddonfield, NJ',
  'Voorhees, NJ',
  'Moorestown, NJ',
  'Abington Township',
];

export default function ServiceAreas({
  heading = 'Serving Greater Philadelphia',
  description = 'TINTSPRO X provides professional automotive, residential, and commercial window tinting across Philadelphia and the surrounding region. Premium film, expert installation, and fast scheduling.',
  areas = defaultAreas,
}: ServiceAreasProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#080808] py-0 overflow-hidden" ref={ref}>
      <div className="flex flex-col lg:flex-row">
        {/* Left content */}
        <div
          className={`w-full lg:w-[420px] shrink-0 px-8 md:px-12 lg:px-16 py-20 flex flex-col justify-center transition-all duration-700 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
          }`}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-[1px] bg-[#AAFF00]" />
            <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">Service Areas</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-[1.1] mb-5">{heading}</h2>
          <p className="text-white/45 font-sans text-sm leading-relaxed mb-8 max-w-xs">{description}</p>

          <div className="flex flex-wrap gap-2 mb-8">
            {areas.map((area, i) => (
              <span
                key={area}
                className={`px-3 py-1.5 rounded-full text-xs font-sans transition-all duration-300 ${
                  i === 0
                    ? 'border border-[#AAFF00] bg-[#AAFF00]/10 text-[#AAFF00]'
                    : 'border border-white/10 text-white/50 hover:border-[#AAFF00]/30 hover:text-white/70'
                }`}
              >
                {area}
              </span>
            ))}
          </div>

          <p className="text-white/30 text-xs font-sans">
            Don&apos;t see your area?{' '}
            <a href="#estimate" className="text-[#AAFF00] hover:underline cursor-pointer">Contact us</a>
            {' '}— we cover many more locations.
          </p>
        </div>

        {/* Right: large map */}
        <div
          className={`flex-1 min-h-[480px] lg:min-h-0 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
          }`}
        >
          <iframe
            width="100%"
            height="100%"
            frameBorder={0}
            scrolling="no"
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d12227.135362234749!2d-75.0915358!3d39.9909357!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6b7d5c637f919:0xd22da67ed2237589!2sTINTSPRO%20X!5e0!3m2!1sen!2s!4v1727820651554!5m2!1sen!2s"
            title="TINTSPRO X Location - Philadelphia PA"
            allowFullScreen
            style={{ minHeight: '480px', filter: 'invert(90%) hue-rotate(180deg) brightness(0.85) saturate(0.6)' }}
          />
        </div>
      </div>

      {/* Address strip */}
      <div className="bg-[#0f0f0f] border-t border-white/5 px-6 md:px-12 lg:px-16 py-5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-fill text-[#AAFF00] text-sm"></i>
            </div>
            <span className="text-white/50 text-sm font-sans">3001 Ormes St Unit 05, Philadelphia, PA 19134</span>
          </div>
          <a
            href="tel:2155949404"
            className="flex items-center gap-2 text-[#AAFF00] text-sm font-sans font-medium hover:text-[#C4FF33] transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-sm"></i>
            </div>
            (215) 594-9404
          </a>
        </div>
      </div>
    </section>
  );
}
